import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Outlet, Link } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Paper,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  IconButton,
  CircularProgress,
  LinearProgress,
  Tooltip,
  useMediaQuery,
  useTheme
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import MenuIcon from '@mui/icons-material/Menu';
import BusinessIcon from '@mui/icons-material/Business';
import DescriptionIcon from '@mui/icons-material/Description';
import AssignmentIcon from '@mui/icons-material/Assignment';
import GroupIcon from '@mui/icons-material/Group';
import BuildIcon from '@mui/icons-material/Build';
import StraightenIcon from '@mui/icons-material/Straighten';
import FindInPageIcon from '@mui/icons-material/FindInPage';
import CloseIcon from '@mui/icons-material/Close';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { getProject, getPhaseProgress } from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';

const drawerWidth = 280;
const collapsedWidth = 64;

const PHASE_COLOR = '#4CAF50';

const discoverProcesses = [
  { id: 'rfp-analysis', name: 'RFP Analysis', description: 'AI-powered analysis of Request for Proposal documents', icon: <FindInPageIcon />, path: 'rfp-analysis' },
  { id: 'business-assessment', name: 'Business Assessment', description: 'Analyze current business state and assessment goals', icon: <BusinessIcon />, path: 'business-assessment' },
  { id: 'current-landscape-analysis', name: 'Current Landscape Analysis', description: 'Assess the current technology and system landscape', icon: <DescriptionIcon />, path: 'current-landscape-analysis' },
  { id: 'requirements-gathering', name: 'Requirements Gathering', description: 'Gather and document functional and non-functional requirements', icon: <AssignmentIcon />, path: 'requirements-gathering' },
  { id: 'stakeholder-interviews', name: 'Stakeholder Interviews', description: 'Conduct and document stakeholder interviews and findings', icon: <GroupIcon />, path: 'stakeholder-interviews' },
  { id: 'technology-assessment', name: 'Technology Assessment', description: 'Evaluate current technology stack and recommendations', icon: <BuildIcon />, path: 'technology-assessment' },
  { id: 'feasibility-study', name: 'Feasibility Study', description: 'Analyze technical, business, and financial feasibility', icon: <StraightenIcon />, path: 'feasibility-study' }
];

const DiscoveryPhaseLayout = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [subphaseProgress, setSubphaseProgress] = useState({});
  const [currentProcess, setCurrentProcess] = useState('');

  const currentDrawerWidth = collapsed ? collapsedWidth : drawerWidth;

  useEffect(() => {
    const selectedProject = localStorage.getItem('selectedProject');
    if (selectedProject) {
      try {
        const project = JSON.parse(selectedProject);
        const selectedProjectId = project.id ? project.id.toString() : null;
        if (selectedProjectId && projectId !== selectedProjectId) {
          const currentPath = window.location.pathname.split('/').slice(3).join('/');
          navigate(`/projects/${selectedProjectId}/${currentPath}`, { replace: true });
        }
      } catch (e) {
        console.error('Error parsing selected project:', e);
      }
    }
  }, [projectId, navigate]);

  useEffect(() => {
    if (!projectId || projectId === ':projectId') return;
    getPhaseProgress(projectId).then(res => {
      if (res.success && res.subphase_progress) setSubphaseProgress(res.subphase_progress);
    });
  }, [projectId]);

  useEffect(() => {
    if (projectId && currentUser) {
      fetchProject();
    }
  }, [projectId, currentUser]);

  useEffect(() => {
    const currentPath = window.location.pathname;
    const processMatch = discoverProcesses.find(process => currentPath.includes(process.path));
    if (processMatch) setCurrentProcess(processMatch.id);
  }, [window.location.pathname]);

  const fetchProject = async () => {
    try {
      setLoading(true);
      const response = await getProject(projectId);
      if (response.success) setProject(response.project);
    } catch (error) {
      console.error('Error fetching project:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);

  const handleProcessClick = (process) => {
    navigate(`/projects/${projectId}/discovery/${process.path}`);
    setCurrentProcess(process.id);
    setMobileOpen(false);
  };

  const drawer = (
    <div>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: collapsed ? 'center' : 'space-between', p: 1, minHeight: 56 }}>
        {!collapsed && (
          <Box sx={{ display: 'flex', alignItems: 'center', pl: 1 }}>
            <AutoAwesomeIcon sx={{ mr: 1, color: PHASE_COLOR }} />
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: PHASE_COLOR }}>
              Discover Phase
            </Typography>
          </Box>
        )}
        {isMobile ? (
          <IconButton onClick={handleDrawerToggle} size="small">
            <CloseIcon />
          </IconButton>
        ) : (
          <IconButton onClick={() => setCollapsed(!collapsed)} size="small">
            {collapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
          </IconButton>
        )}
      </Box>
      <Divider />
      <List>
        {discoverProcesses.map((process) => (
          <Tooltip key={process.id} title={collapsed ? process.name : ''} placement="right">
            <ListItem
              button
              onClick={() => handleProcessClick(process)}
              selected={currentProcess === process.id}
              sx={{
                '&.Mui-selected': { backgroundColor: 'rgba(76, 175, 80, 0.12)', borderRight: collapsed ? 0 : 3, borderColor: PHASE_COLOR, '&:hover': { backgroundColor: 'rgba(76, 175, 80, 0.20)' } },
                '&:hover': { backgroundColor: 'rgba(76, 175, 80, 0.08)' },
                mb: 1,
                mx: collapsed ? 0.5 : 1,
                borderRadius: 1,
                justifyContent: collapsed ? 'center' : 'flex-start',
                px: collapsed ? 1 : 2
              }}
            >
              <ListItemIcon sx={{ color: currentProcess === process.id ? PHASE_COLOR : 'inherit', minWidth: collapsed ? 0 : 56, justifyContent: 'center' }}>
                {process.icon}
              </ListItemIcon>
              {!collapsed && (
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <ListItemText
                  primary={process.name}
                  secondary={process.description}
                  primaryTypographyProps={{ fontWeight: currentProcess === process.id ? 600 : 400, color: currentProcess === process.id ? PHASE_COLOR : 'inherit' }}
                  secondaryTypographyProps={{ fontSize: '0.75rem' }}
                    sx={{ mb: 0.5 }}

                  />
                  <LinearProgress
                    variant="determinate"
                    value={subphaseProgress[process.path] ?? 0}
                    sx={{ height: 3, borderRadius: 1, bgcolor: 'rgba(0,0,0,0.08)' }}
                  />
                </Box>

              )}
            </ListItem>
          </Tooltip>
        ))}
      </List>
    </div>
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <Box
        component="nav"
        sx={{ width: { md: currentDrawerWidth }, flexShrink: { md: 0 }, zIndex: (theme) => theme.zIndex.appBar - 1, transition: theme.transitions.create('width', { easing: theme.transitions.easing.sharp, duration: theme.transitions.duration.enteringScreen }) }}
        aria-label="discover phase processes"
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth, mt: '100px' },
          }}
        >
          {drawer}
        </Drawer>

        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: currentDrawerWidth,
              mt: '100px',
              height: 'calc(100vh - 100px)',
              position: 'fixed',
              overflowX: 'hidden',
              transition: theme.transitions.create('width', { easing: theme.transitions.easing.sharp, duration: theme.transitions.duration.enteringScreen })
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      {isMobile && !mobileOpen && (
        <IconButton
          color="primary"
          aria-label="open drawer"
          onClick={handleDrawerToggle}
          sx={{ position: 'fixed', left: 16, top: 80, zIndex: (theme) => theme.zIndex.drawer + 1, bgcolor: 'background.paper', boxShadow: 2, '&:hover': { bgcolor: 'background.paper' } }}
        >
          <MenuIcon />
        </IconButton>
      )}

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { md: `calc(100% - ${currentDrawerWidth}px)` },
          ml: { md: 0 },
          mt: 2,
          minHeight: '100vh',
          transition: theme.transitions.create(['width', 'margin'], { easing: theme.transitions.easing.sharp, duration: theme.transitions.duration.enteringScreen })
        }}
      >
        <Container maxWidth="xl">
          {project && (
            <Box sx={{ mb: 3 }}>
              <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="h4" gutterBottom sx={{ color: PHASE_COLOR, fontWeight: 600 }}>
                  Discover Phase
                </Typography>
                <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 2 }}>
                  Initial Assessment & Discover for {project.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  The Discover phase involves comprehensive assessment of current business processes,
                  technology landscape, requirements gathering, stakeholder interviews, technology evaluation,
                  and feasibility analysis to establish a solid foundation for your SAP implementation project.
                </Typography>
              </Paper>
            </Box>
          )}
          <Outlet context={{ project, projectId }} />
        </Container>
      </Box>
    </Box>
  );
};

export default DiscoveryPhaseLayout;
